import React, {
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
  type ReactElement,
  useContext,
} from 'react';
import { BOOKMARK_DATA, bookmarkReducer } from './reducer';
import { useQuery } from '@tanstack/react-query';
import { getBookmarks } from '../../api/bookmark';
import { getConfig } from '../../utils/config';
import { BOOKMARKS_SET } from '../actions';
import { DFContext } from '../df-context';
import { initialBookmarkState } from '../state';
import { useLoader } from '../../hooks/use-loader';
import { useLogin } from '../../hooks/use-login';
import { readBookmarksFromStorage } from '../../utils/bookmark';
import { CACHE_TIME } from '../../constants';
import { useChunkedBookmarks } from './use-chunked-bookmarks';
import { ErrorContext } from '../../api/error-boundary';
import { useInitAttributes } from '../../hooks/use-init-attributes';

export function BookmarkProvider({ children }: { children: ReactNode }): ReactElement {
  const [state, dispatch] = useReducer(bookmarkReducer, initialBookmarkState);
  const [currentPage, setCurrentPage] = React.useState(0);
  const { bookmarks } = state;
  const setLoginStatus = useLogin(dispatch);
  const pimLanguage = getConfig('pim_language', 'en');
  const cmsNativeLanguage = getConfig('cms_native_language', 'EN-EN');
  const errorContext = useContext(ErrorContext);
  const initDownloadAttributes = useInitAttributes(pimLanguage, dispatch);

  const updateBookmarks = () => {
    const newBookmarks = readBookmarksFromStorage();
    dispatch({
      type: BOOKMARKS_SET,
      bookmarks: newBookmarks,
    });
  };

  useEffect(() => {
    initDownloadAttributes();
    // sync app state when several browser tabs are opened
    window.addEventListener('focus', updateBookmarks);
    return () => {
      window.removeEventListener('focus', updateBookmarks);
    };
  }, [initDownloadAttributes]);

  const { chunkedBookmarks, maxPages } = useChunkedBookmarks(bookmarks);

  const { data, isLoading, isFetching, status, isError, error } = useQuery({
    queryKey: ['bookmarks', currentPage],
    queryFn: () => getBookmarks(chunkedBookmarks[currentPage], pimLanguage, cmsNativeLanguage),
    staleTime: Infinity,
    gcTime: CACHE_TIME,
    retry: false,
  });

  useEffect(() => {
    if (isError) {
      errorContext?.setError(error.message);
    }
  }, [error, isError, errorContext]);

  useEffect(() => {
    if (data && data.data && status === 'success') {
      dispatch({
        type: BOOKMARK_DATA,
        data: data.data,
      });
    }
  }, [data, status]);

  const showLoader = useLoader(isLoading);

  const value = useMemo(() => {
    return {
      state: {
        ...state,
        isLoading: showLoader,
        isFetching,
        loadMore: currentPage < maxPages,
      },
      fetchNextPage: () => setCurrentPage((prev) => prev + 1),
      dispatch,
      setLoginStatus,
    };
  }, [state, showLoader, isFetching, currentPage, maxPages, setLoginStatus]);

  return <DFContext.Provider value={value}>{children}</DFContext.Provider>;
}
